/**
 * The undo: delete exactly what the import created, and nothing else.
 *
 * Set apart from the rest of the tab on purpose - its own card, a real gap
 * above it, the only danger-toned buttons on the page - because everything
 * above this point adds rows and this is the one control that removes them. No
 * red rule around the card: `--crm-hair` is the only line this design has, and
 * inventing a second one to mean "careful" would be the first of many.
 *
 * THE DRY RUN IS NOT OPTIONAL. The real button does not exist until a plan has
 * been drawn and read; that is the only way an operator can find out, before
 * pressing anything, that a handful of rows will be kept back because their own
 * newer work is attached to them. A confirm step on its own would only ask
 * "are you sure" about a question nobody has been shown the answer to.
 *
 * What it deletes is what the ledger says the import CREATED. A row the import
 * merely updated belongs to whoever made it first and stays. A row somebody
 * typed in here has no ledger entry at all and is invisible to this button.
 */
import { useEffect, useState } from "react";
import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { revertImport, type ImportFootprint, type RevertResult } from "../../../../lib/crmImport";
import { Btn, Card, CardHead, fmtNum } from "../../../ui";
import { RevertReport } from "./RevertReport";

export function RevertCard({
  footprint,
  disabled,
  onDone,
}: {
  /** How many rows the ledger owns per model, from the status endpoint. */
  footprint: ImportFootprint[];
  /** True while a run is going: an undo must not race an import. */
  disabled: boolean;
  /** The counts upstairs changed - the tab reloads them. */
  onDone: () => void;
}) {
  const t = useT();
  const [plan, setPlan] = useState<RevertResult | null>(null);
  const [result, setResult] = useState<RevertResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const owned = footprint.reduce((sum, f) => sum + f.rows, 0);

  // A plan describes a ledger, and the ledger has just moved. Dropping it means
  // the real button disappears too, which is the point: a plan drawn before the
  // last import would promise to delete a set that no longer exists.
  const ledger = footprint.map((f) => `${f.model}:${f.rows}`).join(",");
  useEffect(() => {
    setPlan(null);
    setConfirming(false);
  }, [ledger]);

  async function run(dryRun: boolean) {
    setBusy(true);
    setFailed(false);
    try {
      const answer = await revertImport({ dryRun });
      if (dryRun) {
        setPlan(answer);
      } else {
        setResult(answer);
        setPlan(null);
        onDone();
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <Card>
      <CardHead title={t("crmImport.revert.title")} sub={t("crmImport.revert.desc")} />

      <div className="px-6 pb-6 pt-1">
        <p className="text-[13px] leading-relaxed">{t("crmImport.revert.scope")}</p>

        <div className="mt-4">
          <span className="crm-lab !mt-0">{t("crmImport.revert.footprint")}</span>
          {footprint.length === 0 ? (
            <p className="crm-sub mt-1 text-[12.5px]">{t("crmImport.revert.footprintEmpty")}</p>
          ) : (
            <div className="crm-cov-scroll mt-1.5">
              <table className="crm-cov-tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("crmImport.revert.model")}</th>
                    <th scope="col">{t("crmImport.revert.rows")}</th>
                  </tr>
                </thead>
                <tbody>
                  {footprint.map((row) => (
                    <tr key={row.model}>
                      <th scope="row">{row.model}</th>
                      <td>{fmtNum(row.rows)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {plan ? <RevertReport result={plan} planned /> : null}

        {confirming ? (
          <div className="crm-note mt-4">
            <div className="crm-note-t">
              <TriangleAlert className="size-4" />
              {t("crmImport.revert.confirmTitle")}
            </div>
            <p className="crm-note-p">{t("crmImport.revert.confirmBody")}</p>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <Btn onClick={() => setConfirming(false)}>{t("crmImport.actions.cancel")}</Btn>
              <Btn tone="danger" onClick={() => void run(false)} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                {t("crmImport.revert.confirmYes")}
              </Btn>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {/* The real button is rendered only once a plan is on screen. Not
                disabled - absent: a greyed-out control invites hunting for the
                thing that enables it, and the thing that enables it is reading. */}
            {plan ? (
              <Btn tone="danger" onClick={() => setConfirming(true)} disabled={busy || disabled}>
                <Trash2 className="size-4" />
                {t("crmImport.revert.real")}
              </Btn>
            ) : null}
            <Btn tone="dark" onClick={() => void run(true)} disabled={busy || disabled || owned === 0}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <TriangleAlert className="size-4" />}
              {t("crmImport.revert.dryRun")}
            </Btn>
          </div>
        )}

        <p className="crm-sub mt-2 text-right text-[11.5px] leading-snug">
          {t(plan ? "crmImport.revert.planReady" : "crmImport.revert.dryRunFirst")}
        </p>

        {failed ? <p className="mt-3 text-[12.5px] text-destructive">{t("crmImport.revert.error")}</p> : null}

        {result ? (
          <div className="mt-6 border-t border-[var(--crm-hair)] pt-4">
            <span className="crm-lab !mt-0">{t("crmImport.revert.doneTitle")}</span>
            <RevertReport result={result} planned={false} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}
